import nacl from "tweetnacl";

export function generateKeyPair(): {
  publicKey: Uint8Array;
  privateKey: Uint8Array;
} {
  const keyPair = nacl.box.keyPair();
  return {
    publicKey: keyPair.publicKey,
    privateKey: keyPair.secretKey,
  };
}

export async function deriveKeyFromPassword(
  password: string,
  salt: Uint8Array,
): Promise<CryptoKey> {
  const encoder = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    encoder.encode(password),
    "PBKDF2",
    false,
    ["deriveKey"],
  );

  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: salt as BufferSource,
      iterations: 600000,
      hash: "SHA-256",
    },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

export async function encryptPrivateKey(
  privateKey: Uint8Array,
  derivedKey: CryptoKey,
): Promise<{ encrypted: string; iv: string }> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encrypted = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    derivedKey,
    privateKey as BufferSource,
  );

  return {
    encrypted: btoa(String.fromCharCode(...new Uint8Array(encrypted))),
    iv: btoa(String.fromCharCode(...iv)),
  };
}

export async function decryptPrivateKey(
  encryptedB64: string,
  ivB64: string,
  derivedKey: CryptoKey,
): Promise<Uint8Array> {
  const encrypted = Uint8Array.from(atob(encryptedB64), (c) => c.charCodeAt(0));
  const iv = Uint8Array.from(atob(ivB64), (c) => c.charCodeAt(0));

  const decrypted = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv },
    derivedKey,
    encrypted,
  );

  return new Uint8Array(decrypted);
}

export function encodePublicKey(key: Uint8Array): string {
  return btoa(String.fromCharCode(...key));
}

export function decodePublicKey(encoded: string): Uint8Array {
  return Uint8Array.from(atob(encoded), (c) => c.charCodeAt(0));
}

export function encryptDM(
  message: string,
  recipientPublicKey: Uint8Array,
  senderPrivateKey: Uint8Array,
): { ciphertext: string; nonce: string } {
  const nonce = nacl.randomBytes(nacl.box.nonceLength);
  const encoder = new TextEncoder();
  const plaintext = encoder.encode(message);

  const ciphertext = nacl.box(
    plaintext,
    nonce,
    recipientPublicKey,
    senderPrivateKey,
  );

  return {
    ciphertext: btoa(String.fromCharCode(...ciphertext)),
    nonce: btoa(String.fromCharCode(...nonce)),
  };
}

export function decryptDM(
  ciphertextB64: string,
  nonceB64: string,
  senderPublicKey: Uint8Array,
  recipientPrivateKey: Uint8Array,
): string {
  const ciphertext = Uint8Array.from(atob(ciphertextB64), (c) => c.charCodeAt(0));
  const nonce = Uint8Array.from(atob(nonceB64), (c) => c.charCodeAt(0));

  const plaintext = nacl.box.open(
    ciphertext,
    nonce,
    senderPublicKey,
    recipientPrivateKey,
  );

  if (!plaintext) {
    throw new Error("Failed to decrypt message");
  }

  return new TextDecoder().decode(plaintext);
}

export function encryptGroupMessage(
  message: string,
  groupKey: Uint8Array,
): { ciphertext: string; nonce: string } {
  const nonce = nacl.randomBytes(nacl.secretbox.nonceLength);
  const encoder = new TextEncoder();
  const plaintext = encoder.encode(message);

  const ciphertext = nacl.secretbox(plaintext, nonce, groupKey);

  return {
    ciphertext: btoa(String.fromCharCode(...ciphertext!)),
    nonce: btoa(String.fromCharCode(...nonce)),
  };
}

export function decryptGroupMessage(
  ciphertextB64: string,
  nonceB64: string,
  groupKey: Uint8Array,
): string {
  const ciphertext = Uint8Array.from(atob(ciphertextB64), (c) => c.charCodeAt(0));
  const nonce = Uint8Array.from(atob(nonceB64), (c) => c.charCodeAt(0));

  const plaintext = nacl.secretbox.open(ciphertext, nonce, groupKey);

  if (!plaintext) {
    throw new Error("Failed to decrypt group message");
  }

  return new TextDecoder().decode(plaintext);
}

export function generateGroupKey(): Uint8Array {
  return nacl.randomBytes(nacl.secretbox.keyLength);
}

export function encryptGroupKey(
  groupKey: Uint8Array,
  memberPublicKey: Uint8Array,
  myPrivateKey: Uint8Array,
): { encrypted: string; nonce: string } {
  const nonce = nacl.randomBytes(nacl.box.nonceLength);
  const encrypted = nacl.box(groupKey, nonce, memberPublicKey, myPrivateKey);

  return {
    encrypted: btoa(String.fromCharCode(...encrypted!)),
    nonce: btoa(String.fromCharCode(...nonce)),
  };
}

export function decryptGroupKey(
  encryptedB64: string,
  nonceB64: string,
  senderPublicKey: Uint8Array,
  myPrivateKey: Uint8Array,
): Uint8Array {
  const encrypted = Uint8Array.from(atob(encryptedB64), (c) => c.charCodeAt(0));
  const nonce = Uint8Array.from(atob(nonceB64), (c) => c.charCodeAt(0));

  const groupKey = nacl.box.open(encrypted, nonce, senderPublicKey, myPrivateKey);

  if (!groupKey) {
    throw new Error("Failed to decrypt group key");
  }

  return groupKey;
}
